import { useEffect } from "react";

import clsx from "clsx";

import { gql } from "@apollo/client";
import { GenericExternalLink } from "@components/generic-external-link";
import { useNorthbeamMutation } from "@hooks/http";
import { type RefreshDNSAMIs } from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { useOnboardingStep } from "@pages/onboarding/onboarding-nav/use-onboarding-number";
import { PrimaryButton } from "@shared/buttons";
import Spinner from "@shared/spinner";

import { ConfirmCheckbox } from "../components/confirm-checkbox";
import { InfoPanel } from "../components/info-panel";

export const useGetVerifySetupDnsTitle = () => {
  const { stepDictionary } = useOnboardingStep();
  const stepIndex = stepDictionary["dns-setup"].index;
  return `${stepIndex}c. Confirm DNS is connected and verify setup`;
};

export const VerifySetup = ({
  subStepRef,
  isSetupConfirmed,
  setSetupConfirmed,
  isDNSConnected,
  setDNSConnected,
  disabled,
}: {
  subStepRef: (node: any) => void;
  isSetupConfirmed: boolean;
  setSetupConfirmed: (isConfirmed: boolean) => void;
  isDNSConnected?: boolean;
  setDNSConnected: (connected: boolean) => void;
  disabled?: boolean;
}) => {
  const title = useGetVerifySetupDnsTitle();
  const [refreshAMIs, { loading, data }] = useNorthbeamMutation<RefreshDNSAMIs>(
    REFRESH_AMIS,
    { useHeavy: true },
  );
  useEffect(() => {
    if (loading || !data) {
      return;
    }
    setDNSConnected(data.refreshDNSAMI);
  }, [loading, data]);

  const isVerified = isDNSConnected || data?.refreshDNSAMI;

  return (
    <div
      ref={subStepRef}
      className="pb-20 border-b border-gray-0 px-10 pt-10 scroll-m-10"
    >
      <div className="text-2xl font-semibold leading-loose">{title}</div>
      <div>
        <div className="mb-2 text-lg font-semibold leading-loose">
          Confirm DNS has been configured
        </div>
        <ConfirmCheckbox
          id="confirm-dns-connected"
          checked={isSetupConfirmed}
          onChange={(checked: boolean) => {
            if (checked) {
              refreshAMIs();
            } else {
              // reset dns check when confirmation is unchecked
              setDNSConnected(false);
            }
            setSetupConfirmed(checked);
          }}
          disabled={disabled}
        >
          DNS is configured
        </ConfirmCheckbox>
      </div>
      <div className="mt-6">
        <div className="mb-2 text-lg font-semibold leading-loose inline-flex gap-2">
          <span>DNS connection verification</span>
          <div
            className={clsx(
              "px-2 rounded-3xl justify-start items-center gap-1 inline-flex uppercase font-semibold text-sm",
              {
                "bg-interstellar-3": !isSetupConfirmed || loading,
                "bg-alien-2": isVerified && !loading && isSetupConfirmed,
                "bg-mars-2": !isVerified && !loading && isSetupConfirmed,
              },
            )}
          >
            <StatusIcon
              isVerified={isVerified}
              isSetupConfirmed={isSetupConfirmed}
              loading={loading}
            />
            <span>
              {(!isSetupConfirmed || loading) && "pending"}
              {isSetupConfirmed && isVerified && "success"}
              {isSetupConfirmed && !loading && !isVerified && "failed"}
            </span>
          </div>
        </div>
        {!isSetupConfirmed && (
          <div>
            After your DNS configuration is confirmed, we will verify your DNS
            connection.
          </div>
        )}
        {isSetupConfirmed && (
          <div>
            {loading && (
              <div className="flex gap-2 items-center">
                <Spinner colorClassname="font-black" />
                <span>
                  Verifying DNS is connected. This may take a few minutes,
                  please stay on the page until verification is complete.
                </span>
              </div>
            )}
            {!loading && (
              <>
                <div className="flex gap-2 items-center justify-start font-semibold">
                  <StatusIcon
                    isVerified={isDNSConnected}
                    isSetupConfirmed={isSetupConfirmed}
                    loading={loading}
                  />
                  {isDNSConnected ? (
                    <span>DNS is connected</span>
                  ) : (
                    <span>DNS is not configured correctly</span>
                  )}
                </div>
                {!isDNSConnected && (
                  <div className="w-full max-w-[800px] my-2 py-4 px-4 border rounded bg-sun-2 leading-normal font-semibold">
                    <i className="mr-2 fa-regular fa-warning text-sun+2 font-black" />
                    DNS is not connected to Northbeam
                  </div>
                )}
              </>
            )}
            {!loading && isVerified !== undefined && !isVerified && (
              <div className="mt-4">
                <div className="text-lg">
                  We were not able to determine that DNS is correctly
                  configured. Please verify all steps have been completed and
                  try again.
                </div>
                <PrimaryButton
                  className="my-1 w-fit font-semibold uppercase"
                  onClick={() => {
                    refreshAMIs();
                  }}
                  disabled={loading}
                >
                  <i className="mr-2 fa-sharp fa-regular fa-rotate font-black" />
                  Retry
                </PrimaryButton>
                <InfoPanel
                  label="Need help with verifying your DNS configuration?"
                  className="mt-2 py-4 px-6"
                >
                  <GenericExternalLink
                    href="https://www.northbeam.io/submit-a-support-ticket"
                    className="border border-gray-0 bg-white px-4 py-[6px] inline-flex items-center gap-2 rounded-2xl no-underline
                  hover:bg-cosmic-3 hover:border-cosmic+1"
                  >
                    <i
                      className="fa-envelope fa-regular"
                      style={{ color: "#7081FF" }}
                    ></i>
                    <div className="text-black uppercase font-semibold text-xs">
                      Contact us
                    </div>
                  </GenericExternalLink>
                </InfoPanel>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const StatusIcon = ({
  isVerified,
  isSetupConfirmed,
  loading,
}: {
  isVerified?: boolean;
  isSetupConfirmed: boolean;
  loading: boolean;
}) => (
  <i
    className={clsx("text-base", {
      "fa-regular fa-x text-mars+2 font-black":
        !isVerified && isSetupConfirmed && !loading,
      "fa-regular fa-check text-alien+0 font-black":
        isVerified && isSetupConfirmed && !loading,
      "fa-solid fa-circle-notch ": !isSetupConfirmed || loading,
    })}
  />
);

const REFRESH_AMIS = gql`
  mutation RefreshDNSAMIs {
    refreshDNSAMI
  }
`;
